import { useTranslations } from '@vocab/react';
import { Badge, Inline, Text, TooltipRenderer } from 'braid-design-system';

import type { UserPermissions } from 'src/graphql/generated';
import { Permission, PermissionDisplayNames } from 'src/types/types';

import translations from './.vocab';

interface PermissionsProps {
  permissions: UserPermissions;
  accountHasPremiumTalentSearch?: boolean;
}

export const Permissions = ({
  permissions,
  accountHasPremiumTalentSearch,
}: PermissionsProps) => {
  const { t } = useTranslations(translations);

  const applyPositionAccessPermission = (
    prevValue: Permission[],
  ): Permission[] => {
    const hasAllJobAccess = permissions[Permission.HasAllPositionAccess];

    if (hasAllJobAccess) {
      return prevValue.concat(Permission.HasAllPositionAccess);
    }

    return prevValue.concat(Permission.HasSpecificPositionAccess);
  };

  const checkPremiumTalentSearchEnabled = (permission: string | boolean) =>
    permission === Permission.HasPremiumTalentSearch
      ? permission && accountHasPremiumTalentSearch
      : true;

  const permissionsArray: Permission[] = Object.keys(permissions).reduce(
    (validPermissions: Permission[], permission: string) => {
      const userPermission = permission as Permission;

      if (userPermission === Permission.HasAllPositionAccess) {
        return applyPositionAccessPermission(validPermissions);
      }

      return checkPremiumTalentSearchEnabled(permission) &&
        permissions[userPermission as keyof UserPermissions] === true
        ? validPermissions.concat(userPermission)
        : validPermissions;
    },
    [],
  );

  return (
    <Inline space="small">
      {permissionsArray.map((permission, key) => (
        <TooltipRenderer
          id={`user-permission-${key}-${permission}`}
          // @ts-ignore
          tooltip={<Text>{t(`${permission} permission description`)}</Text>}
          key={`user-permission-${key}-${permission}`}
        >
          {({ triggerProps }) => (
            <Badge tone="neutral" {...triggerProps}>
              {t(PermissionDisplayNames[permission] as ReturnType<any>)}
            </Badge>
          )}
        </TooltipRenderer>
      ))}
    </Inline>
  );
};
